import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { AlertTriangle, Clock, Bell, Calendar } from 'lucide-react';
import type { Reminder, ReminderForm, ModalState } from '../types/system-parameters.types';

interface ReminderModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: ReminderForm, isEdit?: boolean, itemId?: string) => void;
}

const frequencyOptions = [
  { value: 'once', label: 'Una sola vez' },
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' }
];

const getFrequencyIcon = (frequency: string) => {
  switch (frequency) {
    case 'daily':
      return <Clock className="h-3 w-3" />;
    case 'weekly':
    case 'monthly':
      return <Calendar className="h-3 w-3" />;
    case 'once':
    default:
      return <Bell className="h-3 w-3" />;
  }
};

export const ReminderModal: React.FC<ReminderModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<ReminderForm>({
    action: '',
    clickAction: '',
    time: '09:00',
    description: '',
    frequency: 'once',
    isEnabled: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const reminder = modalState.item as Reminder;
        setFormData({
          action: reminder.additionalData.action,
          clickAction: reminder.additionalData.clickAction || '',
          time: reminder.additionalData.time,
          description: reminder.additionalData.description,
          frequency: reminder.additionalData.frequency,
          isEnabled: reminder.additionalData.isEnabled
        });
      } else {
        // Resetear para modo crear
        setFormData({
          action: '',
          clickAction: '',
          time: '09:00',
          description: '',
          frequency: 'once',
          isEnabled: true
        });
      }
      setErrors({});
    }
  }, [modalState]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!formData.action.trim()) {
      newErrors.action = 'La acción es obligatoria';
    }

    if (!formData.clickAction.trim()) {
      newErrors.clickAction = 'La acción de clic es obligatoria';
    }

    if (!formData.time.trim()) {
      newErrors.time = 'La hora es obligatoria';
    } else if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.time)) {
      newErrors.time = 'Formato de hora inválido (HH:MM)';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }

    if (!formData.frequency) {
      newErrors.frequency = 'La frecuencia es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const isEdit = modalState.mode === 'edit';
      const itemId = modalState.item?.id;
      
      onSave(formData, isEdit, itemId);
      onClose();
    } catch (error) {
      console.error('Error al guardar recordatorio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ReminderForm, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-macartur-primary" />
            {modalState.mode === 'edit' ? 'Editar Recordatorio' : 'Agregar Nuevo Recordatorio'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Acción */}
          <div className="space-y-2">
            <Label htmlFor="action">
              Acción <span className="text-red-500">*</span>
            </Label>
            <Input
              id="action"
              value={formData.action}
              onChange={(e) => handleInputChange('action', e.target.value)}
              placeholder="Descarte de Pan, Revisar inventario, etc."
              className={errors.action ? 'border-red-500' : ''}
            />
            {errors.action && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.action}
              </p>
            )}
          </div>

          {/* Acción de Clic */}
          <div className="space-y-2">
            <Label htmlFor="clickAction">
              Acción <span className="text-red-500">*</span>
            </Label>
            <Input
              id="clickAction"
              value={formData.clickAction}
              onChange={(e) => handleInputChange('clickAction', e.target.value)}
              placeholder="Ir a movimiento de insumos, Abrir dashboard, etc."
              className={errors.clickAction ? 'border-red-500' : ''}
            />
            {errors.clickAction && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.clickAction}
              </p>
            )}
            <p className="text-xs text-gray-600">
              Acción que se ejecutará cuando se haga clic en la notificación desde el header
            </p>
          </div>

          {/* Hora y Frecuencia */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="time">
                Hora <span className="text-red-500">*</span>
              </Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className={errors.time ? 'border-red-500' : ''}
              />
              {errors.time && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {errors.time}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">
                Frecuencia <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.frequency} 
                onValueChange={(value) => handleInputChange('frequency', value as 'daily' | 'weekly' | 'monthly' | 'once')}
              >
                <SelectTrigger className={errors.frequency ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccionar frecuencia" />
                </SelectTrigger>
                <SelectContent>
                  {frequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        {getFrequencyIcon(option.value)}
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.frequency && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {errors.frequency}
                </p>
              )}
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Descripción <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detalla qué debe recordarse y cualquier información adicional importante..."
              className={errors.description ? 'border-red-500' : ''}
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Estado Habilitado */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="isEnabled" className="text-sm font-medium">
                Recordatorio activo
              </Label>
              <p className="text-xs text-gray-600 mt-1">
                Cuando está activado, se ejecutará según la frecuencia configurada
              </p>
            </div>
            <Switch
              id="isEnabled"
              checked={formData.isEnabled}
              onCheckedChange={(checked) => handleInputChange('isEnabled', checked)}
            />
          </div>

          {/* Preview del recordatorio */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Vista previa del recordatorio</span>
            </div>
            <div className="text-sm text-blue-800">
              <p><strong>{formData.action || 'Acción'}</strong></p>
              <p className="flex items-center gap-1 mt-1">
                <Clock className="h-3 w-3" />
                {formData.time} - {frequencyOptions.find(f => f.value === formData.frequency)?.label}
              </p>
              <p className="mt-1 text-blue-700">{formData.description || 'Descripción del recordatorio'}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-macartur-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (modalState.mode === 'edit' ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};